import React from "react";
import type { Rewards } from "../../../common/jsonRpcTypes";
import { formatCurrency } from "../../../utils/countryLocaleMap";
import { pointsToCurrencyValue } from "../../utils/rewardsEligibility";

/**
 * Render the rewards balance status message below the rewards banner.
 * Shared between BookingDetailsForm and CustomerDetailsForm.
 *
 * The balance comes from the signed-in guest's `points_balance`, which DMN
 * returns on sign-in. Collins does not send a balance with the booking
 * response, so a logged-out guest is prompted to sign up instead.
 */
export function renderRewardsBalanceMessage(
  rewards: Rewards,
  currency: string,
  countryCode: string,
  t: (key: string, options?: Record<string, unknown>) => string,
  isLoggedIn?: boolean,
  pointsBalance?: number,
): React.ReactNode {
  if (pointsBalance == null) {
    if (isLoggedIn) return null;
    return <p className="gcw:mt-1">{t("booking_rewards_login_prompt")}</p>;
  }

  const balanceValue = pointsToCurrencyValue(pointsBalance, rewards);
  const formattedBalanceValue = balanceValue == null ? null : formatCurrency(balanceValue, currency, countryCode);
  const { redeemMinimumAmount, redeemMinimumValue } = rewards;

  if (redeemMinimumAmount != null && pointsBalance >= redeemMinimumAmount) {
    return (
      <p className="gcw:mt-1">
        {t("booking_rewards_can_redeem", {
          balance: pointsBalance,
          balanceValue: formattedBalanceValue,
        })}
      </p>
    );
  }

  if (redeemMinimumAmount != null) {
    return (
      <p className="gcw:mt-1">
        {t("booking_rewards_below_minimum", {
          balance: pointsBalance,
          balanceValue: formattedBalanceValue,
          minimumAmount: redeemMinimumAmount,
          minimumValue: formatCurrency(redeemMinimumValue ?? 0, currency, countryCode),
        })}
      </p>
    );
  }

  return null;
}
